import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Svg, SVG_ICONS } from "../../components/__common/Svg/Svg";
import { ROUTES_CONSIGNMENT_PAGE } from "../../configs/layout";
import { ROUTES } from "../../configs/routes";
import {
  EVENT_ACTION_MANAGE_COOKIES,
  EVENT_CATEGORY_FOOTER,
  EVENT_FOOTER_ACTION_COOKIE_POLICY,
  EVENT_FOOTER_ACTION_PRIVACY_POLICY,
} from "../../constants/analytics";
import { IS_PRE, IS_PROD } from "../../constants/environment";
import { useCookie } from "../../hooks/cookie-hooks";
import { useLanguage } from "../../hooks/language-hooks";
import { useLayout } from "../../hooks/layout-hooks";
import { useWindowSize } from "../../hooks/window-hooks";
import { ApplicationState } from "../../store";
import { LayoutState } from "../../store/reducers/layout-reducer";
import { SystemState } from "../../store/reducers/site-reducer";
import { UserState } from "../../store/reducers/user-reducer";
import GoogleAnalytics from "../../utils/GoogleAnalytics";
import { UtilsDateTime } from "../../utils/UtilsDateTIme";
import { routeShouldRenderComponent } from "../../utils/UtilsLayout";
import FocusTrapping from "../__hoc/FocusTrapping";
import { ManageCookieModal } from "../CookieModal/ManageCookie";
import { Container } from "../Grid/Grid";
import { LanguageModal } from "../LanguageModal/LanguageModal";
import "./Footer.scss";
import { FooterMobile } from "./FooterMobile";
import ClickableElement from "../__common/_controls/Button/ClickableElement";

export const Footer: React.FC = () => {
  const { isMobile } = useWindowSize();
  const { setManageCookieModalOpen } = useCookie();
  const { isLanguageModalShow, setIsLanguageModalShow } = useLanguage();
  const layoutState = useSelector<ApplicationState, LayoutState>(
    (state) => state.layout
  );

  const { country, language, isManageCookieOpen } = layoutState;

  const handleManageCookieFocusOnPolicyPage = () => {
    if (sessionStorage.getItem("MANAGE_COOKIE_FOOTER_CLICKED") === "true") {
      document.getElementById("footer-manage-cookies-btn")?.focus();
      sessionStorage.removeItem("MANAGE_COOKIE_FOOTER_CLICKED");
    } else {
      document.getElementById("cookie-policy-manage-cookies-btn")?.focus();
    }
  };

  const handleCookieModalClose = () => {
    setManageCookieModalOpen(false);
    if (window.location.pathname === ROUTES.COOKIE_POLICY) {
      handleManageCookieFocusOnPolicyPage();
    } else {
      document.getElementById("footer-manage-cookies-btn")?.focus();
    }
  };

  useEffect(() => {
    if (isLanguageModalShow) {
      document.getElementById("language-modal-tts")?.focus();
    }
  }, [isLanguageModalShow]);

  const handleCloseLanguageModal = () => {
    setIsLanguageModalShow(false);

    // NOTE:
    // Find an ideal solution for this.
    // But, for now, this fix will do for PLM ticket SDAP-4263
    setTimeout(() => {
      document.getElementById("footer-language-button")?.focus();
    }, 500);
  };

  return (
    <>
      {isManageCookieOpen && (
        <FocusTrapping
          testId="cookie-modal-focus-trapping"
          isActive={isManageCookieOpen}
          onHandleClose={handleCookieModalClose}
          modalId="manage-cookie"
        >
          <ManageCookieModal
            id="manage-cookie"
            isOpen={isManageCookieOpen}
            onHide={handleCookieModalClose}
          />
        </FocusTrapping>
      )}
      <FocusTrapping
        testId="language-modal-focus-trapping"
        isActive={isLanguageModalShow}
        onHandleClose={handleCloseLanguageModal}
        modalId="language-modal-footer"
      >
        <LanguageModal
          headertestid="language-modal-hide-btn"
          id="language-modal-footer"
          isSiteLanguage
          show={isLanguageModalShow}
          onHide={handleCloseLanguageModal}
        />
      </FocusTrapping>
      {!isMobile ? (
        <FooterDesktop
          onClickLanguage={() => setIsLanguageModalShow(true)}
          onClickManageCookie={() => setManageCookieModalOpen(true)}
          country={country}
          language={language}
        />
      ) : (
        <FooterMobile
          testId="footer-mobile"
          onClickLanguage={() => setIsLanguageModalShow(true)}
          onClickManageCookie={() => setManageCookieModalOpen(true)}
          country={country}
          language={language}
        />
      )}
    </>
  );
};

interface DesktopProps {
  onClickLanguage: () => void;
  onClickManageCookie: () => void;
  country?: string;
  language?: string;
}

export const FooterDesktop: React.FC<DesktopProps> = (props) => {
  const { t } = useTranslation();
  const { setTheme } = useLayout();
  const { pathname } = useLocation();
  const [isVersionShowing, setIsVersionShowing] = useState<boolean>(true);
  const shouldShowVersion = !IS_PROD && !IS_PRE && isVersionShowing;
  const {
    sysProps: { BUILD_TIMESTAMP, BUILD_VERSION },
  } = useSelector<ApplicationState, SystemState>((state) => state.system);

  const { isDarkMode } = useSelector<ApplicationState, LayoutState>(
    (state) => state.layout
  );
  const userState = useSelector<ApplicationState, UserState>(
    (state) => state.user
  );

  const { userRegion } = userState;
  const handleLanguageClick = () => {
    props.onClickLanguage();
  };

  const handleManageCookieClick = () => {
    sessionStorage.setItem("MANAGE_COOKIE_FOOTER_CLICKED", "true");
    GoogleAnalytics.triggerEvent(
      EVENT_CATEGORY_FOOTER,
      EVENT_ACTION_MANAGE_COOKIES
    );
    props.onClickManageCookie();
  };

  return (
    <div
      className={classNames("footer", {
        "snap-scroll": pathname === ROUTES.ROOT,
        "consignment-footer": ROUTES_CONSIGNMENT_PAGE.includes(pathname),
      })}
    >
      <Container className="footer__content">
        <div className="footer__content__left">
          <div>
            {!routeShouldRenderComponent(pathname, ROUTES_CONSIGNMENT_PAGE) && (
              <div className="footer__content__left__links">
                <Link
                  data-testid="link-to-privacy-policy"
                  className="footer__link sharp-sans"
                  to={ROUTES.PRIVACY_POLICY}
                  onClick={() =>
                    GoogleAnalytics.triggerEvent(
                      EVENT_CATEGORY_FOOTER,
                      EVENT_FOOTER_ACTION_PRIVACY_POLICY
                    )
                  }
                >
                  {t("PRIVACY_HEADER_MENU_PRIVACY_POLICY")}
                </Link>
                {userRegion.isFromEurope && (
                  <>
                    <span className="divider"></span>
                    <Link
                      title={t("PRIVACY_FOOTER_MENU_COOKIES")}
                      data-testid="link-to-cookie-policy"
                      className="footer__link sharp-sans"
                      to={ROUTES.COOKIE_POLICY}
                      onClick={() =>
                        GoogleAnalytics.triggerEvent(
                          EVENT_CATEGORY_FOOTER,
                          EVENT_FOOTER_ACTION_COOKIE_POLICY
                        )
                      }
                    >
                      {t("PRIVACY_FOOTER_MENU_COOKIES")}
                    </Link>

                    <span className="divider"></span>
                    {/* eslint-disable-next-line */}
                    <Link
                      id="footer-manage-cookies-btn"
                      role="button"
                      to={"."} // stays on current path
                      className="footer__link sharp-sans"
                      data-testid="handle-cookie-button"
                      onClick={(e) => {
                        e.preventDefault();
                        handleManageCookieClick();
                      }}
                    >
                      {t("PRIVACY_FOOTER_MENU_MANAGE_COOKIES")}
                    </Link>
                  </>
                )}

                <span className="divider"></span>

                {/* change language button */}
                <button
                  data-testid="handle-language-button"
                  className="footer__link sharp-sans"
                  onClick={handleLanguageClick}
                  id="footer-language-button"
                >
                  <span className="country">{props.country}</span> /{" "}
                  <span>{props.language}</span>
                  <Svg
                    className="arrow-right"
                    width={16}
                    height={16}
                    icon={SVG_ICONS.ARROW_RIGHT}
                  />
                </button>
              </div>
            )}

            <div className="footer__content__left__copyright sharp-sans">
              <span>
                Copyright © Samsung Electronics Co, Ltd. All Rights Reserved
              </span>
              <div className="footer__content__left__links__themes">
                <button
                  data-testid="hc-toggle-button-on"
                  className="icon-link"
                  onClick={() => setTheme(true)}
                >
                  <Svg
                    width={16}
                    height={16}
                    icon={SVG_ICONS.FOOTER_THEME_DARK}
                    title={
                      isDarkMode
                        ? t("PRIVACY_FOOTER_DARKMODE_ON_ALT_TEXT")
                        : t("PRIVACY_FOOTER_SWITCH_DARKMODE_ALT_TEXT")
                    }
                  />
                </button>
                <button
                  data-testid="hc-toggle-button-off"
                  className={classNames("icon-link", "icon-link-last")}
                  onClick={() => setTheme(false)}
                >
                  <Svg
                    width={16}
                    height={16}
                    icon={SVG_ICONS.FOOTER_THEME_DEFAULT}
                    title={
                      !isDarkMode
                        ? t("PRIVACY_FOOTER_LIGHTMODE_ON_ALT_TEXT")
                        : t("PRIVACY_FOOTER_SWITCH_LIGHTMODE_ALT_TEXT")
                    }
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
        {shouldShowVersion && (
          <div
            data-testid="footer-version-container"
            className={classNames("footer__content__version", {
              hc: isDarkMode,
            })}
          >
            <div className="footer__content__version__left">
              <div
                className={classNames("footer__content__version__left__flex", {
                  hc: isDarkMode,
                })}
              >
                <div
                  className={classNames(
                    "footer__content__version__left__flex__badge",
                    {
                      hc: isDarkMode,
                    }
                  )}
                >
                  FE
                </div>
                <p>
                  v.{process.env.REACT_APP_ENVIRONMENT}.
                  {process.env.REACT_APP_VERSION}
                  <span className="fade">
                    -
                    {UtilsDateTime.formatDate(
                      process.env.REACT_APP_TIMESTAMP || ""
                    )}
                  </span>
                </p>
              </div>
              <div
                className={classNames("footer__content__version__left__flex", {
                  hc: isDarkMode,
                })}
              >
                <div
                  className={classNames(
                    "footer__content__version__left__flex__badge",
                    {
                      hc: isDarkMode,
                    }
                  )}
                >
                  BE
                </div>
                <p>
                  {BUILD_VERSION}
                  <span className="fade">
                    -{UtilsDateTime.formatDate(BUILD_TIMESTAMP)}
                  </span>
                </p>
              </div>
            </div>
            <ClickableElement
              testId="hide-version-button"
              className="footer__content__version__right"
              onClick={() => setIsVersionShowing(false)}
            >
              <Svg
                width={14}
                height={14}
                icon={
                  isDarkMode
                    ? SVG_ICONS.FOOTER_VERSION_CLOSE_ICON_HC
                    : SVG_ICONS.FOOTER_VERSION_CLOSE_ICON
                }
                title={t("PRIVACY_COMMON_CLOSE_ALT_TEXT")}
              />
            </ClickableElement>
          </div>
        )}
      </Container>
    </div>
  );
};
